import type { ComponentType, ReactNode, SVGProps } from 'react';
import {
  AdjustmentsHorizontalIcon,
  ChatBubbleBottomCenterTextIcon,
  Cog6ToothIcon,
  PaintBrushIcon,
  TagIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { SectionHeader } from '../../components/ui/section-header';
import { WorkspaceSectionCard } from '../../../components/workspaces/EntityWorkspacePrimitives';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

interface ElementEditorSectionProps {
  enabled: boolean;
  title: string;
  description?: string;
  icon?: IconComponent;
  hasErrors?: boolean;
  headerAction?: ReactNode;
  children: ReactNode;
}

export const elementEditorSectionIcons = {
  configuration: Cog6ToothIcon,
  appearance: PaintBrushIcon,
  customInputs: ChatBubbleBottomCenterTextIcon,
  contactInformation: UserIcon,
  tracking: TagIcon,
  settings: AdjustmentsHorizontalIcon,
} satisfies Record<string, IconComponent>;

export function elementEditorSectionHasErrors(
  invalidFields: Set<string> | undefined,
  section: string,
): boolean {
  if (!invalidFields) return false;
  return Array.from(invalidFields).some((field) => field.startsWith(`${section}-`));
}

export function focusFirstInvalidElement(container: HTMLElement | null): void {
  if (!container) return;

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      const target = container.querySelector<HTMLElement>(
        [
          '[aria-invalid="true"]',
          'input.border-red-500',
          'select.border-red-500',
          'textarea.border-red-500',
          '.border-red-500 input',
          '.border-red-500 select',
          '.border-red-500 textarea',
          '.border-red-500 button',
        ].join(','),
      );

      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.focus({ preventScroll: true });
    });
  });
}

export default function ElementEditorSection({
  enabled,
  title,
  description,
  icon,
  hasErrors = false,
  headerAction,
  children,
}: ElementEditorSectionProps) {
  if (!enabled) {
    return (
      <>
        <SectionHeader title={title} className="mb-0">
          {headerAction}
        </SectionHeader>
        {children}
      </>
    );
  }

  return (
    <WorkspaceSectionCard
      title={title}
      description={description}
      icon={icon}
      headerAction={headerAction}
      hasErrors={hasErrors}
      bodyClassName="space-y-4"
    >
      {children}
    </WorkspaceSectionCard>
  );
}
